import React from 'react';
import { Box, Tooltip, ActionIcon } from '@cowbell/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  TrustLevel,
  type SenderTrustDecision,
} from '../../../../../api/cmail/cmail.dto';
import {
  formatFlagName,
  getSenderTrustIcon,
  getTrustLevelIcon,
  getVerifiedUserIcon,
  isNegativeFlag,
} from '../../../utils/senderTrustUtils';
import classes from './SenderTrustBadge.module.css';

interface SenderTrustBadgeProps {
  readonly senderTrustDecision?: SenderTrustDecision;
  readonly validatedUser?: boolean;
  readonly onLowTrustClick?: () => void;
}

export const SenderTrustBadge: React.FC<SenderTrustBadgeProps> = ({
  senderTrustDecision,
  validatedUser,
  onLowTrustClick,
}) => {
  const display = getSenderTrustIcon(senderTrustDecision, validatedUser);

  if (!display) {
    return null;
  }

  const isLowTrust = senderTrustDecision?.trustLevel === TrustLevel.LOW;
  const verifiedUser = getVerifiedUserIcon(validatedUser);
  const trustLevel =
    senderTrustDecision && getTrustLevelIcon(senderTrustDecision.trustLevel);

  const getReasonColor = (reason: string): string => {
    const lowerReason = reason.toLowerCase();

    if (
      lowerReason.includes('pass for domain') ||
      lowerReason.includes('valid') ||
      lowerReason.includes('tls used')
    ) {
      return 'var(--cmail-trust-green)';
    }
    if (lowerReason.includes('differs') || lowerReason.includes('divergent')) {
      return 'var(--cmail-trust-warning)';
    }
    return 'var(--mantine-color-text)';
  };

  const formatReason = (reason: string): string => {
    return reason
      .replace(/:\s*null\s*$/i, ': N/A')
      .replace(/:\s*null\s*/gi, ': N/A ');
  };

  const getFlagStatus = (flag: string, value: boolean): boolean => {
    const isFlagNegative = isNegativeFlag(flag);
    return isFlagNegative ? !value : value;
  };

  const getFlagColor = (flagPassed: boolean): string => {
    return flagPassed
      ? 'var(--cmail-trust-green)'
      : 'var(--mantine-color-red-6)';
  };

  const tooltipContent = (
    <Box>
      {trustLevel && (
        <Box className={classes.tooltipRow}>
          <Box
            className={classes.iconWrapper}
            style={{ '--icon-color': trustLevel.color } as React.CSSProperties}
          >
            <FontAwesomeIcon
              icon={trustLevel.icon}
              className={classes.dynamicColorIcon}
            />
          </Box>
          <strong>Trust Level:</strong> {senderTrustDecision.trustLevel}
        </Box>
      )}
      {verifiedUser && (
        <Box className={classes.tooltipRow}>
          <Box
            className={classes.iconWrapper}
            style={
              { '--icon-color': verifiedUser.color } as React.CSSProperties
            }
          >
            <FontAwesomeIcon
              icon={verifiedUser.icon}
              className={classes.dynamicColorIcon}
            />
          </Box>
          <strong>Verified User:</strong> {verifiedUser.label}
        </Box>
      )}
      {senderTrustDecision?.alignedDomain && (
        <Box className={classes.section}>
          <strong>Aligned Domain:</strong> {senderTrustDecision.alignedDomain}
        </Box>
      )}
      {senderTrustDecision && senderTrustDecision.reasons.length > 0 && (
        <Box className={classes.section}>
          <Box className={classes.sectionLabel}>Reasons:</Box>
          <Box component="ul" className={classes.list}>
            {senderTrustDecision.reasons.map((reason, index) => (
              <Box
                component="li"
                key={index}
                className={`${classes.listItem} ${classes.dynamicColorIcon}`}
                style={
                  {
                    '--icon-color': getReasonColor(reason),
                  } as React.CSSProperties
                }
              >
                {formatReason(reason)}
              </Box>
            ))}
          </Box>
        </Box>
      )}
      {senderTrustDecision?.flags && (
        <Box className={classes.flagsSection}>
          <Box className={classes.sectionLabel}>Security Checks:</Box>
          <Box component="ul" className={classes.list}>
            {Object.entries(senderTrustDecision.flags).map(([key, value]) => {
              const boolValue = value as boolean;
              const flagPassed = getFlagStatus(key, boolValue);
              const flagColor = getFlagColor(flagPassed);
              return (
                <Box
                  component="li"
                  key={key}
                  className={`${classes.listItem} ${classes.dynamicColorIcon}`}
                  style={{ '--icon-color': flagColor } as React.CSSProperties}
                >
                  {flagPassed ? '✓' : '✗'} {formatFlagName(key, boolValue)}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
      {isLowTrust && (
        <Box className={classes.lowTrustHint}>Click to approve this sender</Box>
      )}
    </Box>
  );

  const badgeContent = (
    <Box className={classes.badgeContent} aria-label="Sender trust">
      <span
        className={classes.badgeIcon}
        style={{ '--icon-color': display.color } as React.CSSProperties}
      >
        <FontAwesomeIcon icon={display.icon} />
      </span>
    </Box>
  );

  return (
    <Tooltip label={tooltipContent} withArrow>
      {isLowTrust && onLowTrustClick ? (
        <ActionIcon variant="transparent" size="sm" onClick={onLowTrustClick}>
          {badgeContent}
        </ActionIcon>
      ) : (
        <span>{badgeContent}</span>
      )}
    </Tooltip>
  );
};
